import * as t from "io-ts";
import { TreeNode } from "@/types/tree";

export type FieldToComplete = {
  nodeId: TreeNode["id"];
  nodeName: string;
  controlPath: string;
  field: {
    label: string;
    description: string;
    value: unknown;
    type: string;
    locked: boolean;
  };
};

// The field that we send to OpenAI
export type FieldToFill = {
  index: number;
  type: string;
  content: string;
};

// The field that OpenAI returns
export type CompletedField = {
  nodeId: TreeNode["id"];
  controlPath: string;
  value: unknown;
};

export const AIPresetV = t.partial({
  icon: t.string,
  name: t.string,
  prompt: t.string
});

export type AIPreset = t.TypeOf<typeof AIPresetV>;

export const PromptSettingsV = t.type({
  businessName: t.union([t.string, t.null]),
  businessDescription: t.union([t.string, t.null]),
  qualifiers: t.union([t.string, t.null]),
  presets: t.array(AIPresetV)
});

export type PromptSettings = t.TypeOf<typeof PromptSettingsV>;

export const AISettingsV = t.type({
  isApiKeySet: t.boolean,
  isStreamingEnabled: t.boolean,
  locale: t.string,
  model: t.string,
  prompt: PromptSettingsV
});

export type AISettings = t.TypeOf<typeof AISettingsV>;

export type PromptInfo = {
  sectionDescription: string;
  qualifiers: string;
  temperature: number;
  locale: string;
  unsplash: {
    enabled: boolean;
    fillEmptyImagesOnly: boolean;
    keywords: string;
  };
  lockedFields: string[];
  singularity: {
    shouldGenerateOnInitialLoad: boolean;
    sectionData: {
      type: string | false;
      index: number | false;
    };
    unsplashKeywords: string[];
  }
  onboarding: {
    sectionType: string | false;
    shouldGenerateOnInitialLoad: boolean;
  };
};

export type ChatMessage = {
  role: "user" | "assistant" | "system";
  content: string;
};

export const UnsplashImageV = t.type({
  id: t.string,
  alt_description: t.union([t.string, t.null]),
  description: t.union([t.string, t.null]),
  urls: t.partial({
    raw: t.string,
    full: t.string,
    regular: t.string,
    small: t.string,
    thumb: t.string
  })
});

export type UnsplashImage = t.TypeOf<typeof UnsplashImageV>;

export type CompletionFormat = "json" | "text" | "css";

export type InlineCompletion = {
  text: string;
  prompt: string;
  loading: boolean;
  expanded: boolean;
};

export const OpenAIErrorV = t.type({
  error: t.partial({
    message: t.string,
    type: t.union([t.string, t.null]),
    param: t.union([t.string, t.null]),
    code: t.union([t.string, t.number])
  })
});

export type OpenAIError = t.TypeOf<typeof OpenAIErrorV>;
